import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Empty, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import MessageTypeHeader from "./MessageTypeHeader";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
// CUSTOM HOOKS
import useGetAllMessagesTypes from "../../../customHooks/GET_requests/useGetAllMessagesTypes";

function MessageTypeTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Column, ColumnGroup } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    showSizeChanger: boolean;
    pageSizeOptions: string[];
  }>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const {
    messagesTypes,
    isLoadingGetAllMessagesTypes,
    errorMessageGetAllMessagesTypes,
    isErrorGetAllMessagesTypes,
    totalRowsGetAllMessagesTypes,
  } = useGetAllMessagesTypes();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleTableChange = (paginationUpdate: {
    current?: number;
    pageSize?: number;
    total?: number;
  }) => {
    setPagination({
      ...pagination,
      current: paginationUpdate.current || pagination.current,
      pageSize: paginationUpdate.pageSize || pagination.pageSize,
    });
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/** ----------------------------------------------------- */}
      {/** ACTION FORM */}
      <div>
        {hasPermission(Permission.PermissionNotDefined, permissionSet ?? []) ? (
          <>
            <MessageTypeHeader />
            <Table
              locale={{
                emptyText: (
                  <TableNoDataBox
                    isError={isErrorGetAllMessagesTypes}
                    errorMessage={errorMessageGetAllMessagesTypes}
                    noItemsFound="No message types found"
                  />
                ),
              }}
              rowClassName={(record, index) => getTableRowClass(index, theme)}
              dataSource={messagesTypes}
              pagination={{
                ...pagination,
                total: totalRowsGetAllMessagesTypes,
              }}
              onChange={handleTableChange}
              size="small"
              className="mt-3"
              loading={isLoadingGetAllMessagesTypes}
              footer={() => (
                <div className="generalStyles__flexEnd">
                  <Tag>{totalRowsGetAllMessagesTypes} Rows</Tag>
                </div>
              )}
            >
              {/** NAME */}
              <ColumnGroup title="Name">
                <Column
                  dataIndex="name"
                  key="name"
                  render={(text: string) => <span>{text}</span>}
                />
              </ColumnGroup>
              {/** COLOR */}
              <ColumnGroup title="Color">
                <Column
                  dataIndex="color"
                  key="color"
                  render={(text: string) => (
                    <Tag
                      className="generalStyles__width100px generalStyles__textCenter"
                      color={text}
                    >
                      {text}
                    </Tag>
                  )}
                />
              </ColumnGroup>
            </Table>
          </>
        ) : (
          <div className="generalStyles__noAccessToListTable">
            <Empty
              description={t("organizationManagement.listNotAvailableOrganizations")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageTypeTable;
